import { useEffect, useRef, useState } from "react";
import api, { formatApiError, fileToBase64 } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Building2, Upload, Save, Loader2, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { k: "company_name", label: "Nome azienda", ph: "86BIT srl Unipersonale", section: "brand" },
  { k: "legal_name", label: "Ragione sociale (footer)", ph: "86BIT srl Unipersonale" },
  { k: "vat", label: "Partita IVA", ph: "04353030168" },
  { k: "fiscal_code", label: "Codice Fiscale", ph: "04353030168" },
  { k: "share_capital", label: "Capitale sociale", ph: "€ 30.000,00 i.v." },
  { k: "reg_imprese", label: "Reg. Imprese di", ph: "Bergamo 04353030168" },
  { k: "rea", label: "REA n.", ph: "BG456578" },
  { k: "address", label: "Indirizzo / Sede", ph: "Piazza Papa Giovanni XXIII - 24020 Scanzorosciate (BG)" },
  { k: "phone", label: "Telefono", ph: "+39 035 310 900" },
  { k: "email", label: "Email", ph: "info@86bit.it" },
  { k: "website", label: "Sito web", ph: "www.86bit.it" },
];

export default function Company() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const sigRef = useRef();

  useEffect(() => {
    api.get("/company").then((r) => setForm(r.data)).catch(() => setForm({}));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Il logo deve essere inferiore a 2MB");
      return;
    }
    const b64 = await fileToBase64(file);
    update("logo", b64);
  };

  const onSignature = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Il timbro/firma deve essere inferiore a 2MB");
      return;
    }
    const b64 = await fileToBase64(file);
    update("signature_image", b64);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", form);
      toast.success("Profilo azienda salvato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore di salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Layout><p className="text-slate-500">Caricamento…</p></Layout>;

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-blue-600" /> La mia azienda
          </h1>
          <p className="text-slate-500 mt-1">Logo e dati fiscali usati su tutti i preventivi.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-company-button" className="bg-blue-600 hover:bg-blue-700">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Logo & Brand</h2>
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 flex flex-col items-center justify-center min-h-[140px] mb-4">
            {form.logo ? (
              <div className="relative">
                <img src={form.logo} alt="Logo" className="max-h-24 max-w-full" data-testid="company-logo-preview" />
                <button onClick={() => update("logo", "")} className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-1 text-slate-500 hover:text-red-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="text-center text-slate-400">
                <ImageIcon className="w-8 h-8 mx-auto mb-1" />
                <span className="text-xs">Nessun logo</span>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="company-logo-input" />
          <Button variant="outline" className="w-full" onClick={() => fileRef.current?.click()} data-testid="company-logo-upload-btn">
            <Upload className="w-4 h-4 mr-2" /> Carica logo (PNG/JPG)
          </Button>

          <div className="mt-6 pt-5 border-t border-slate-200">
            <Label className="text-xs">Timbro e firma azienda (PNG)</Label>
            <p className="text-xs text-slate-400 mt-1 mb-2">Apparirà nella pagina di chiusura, sopra la firma del fornitore. Consigliato PNG con sfondo trasparente.</p>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 flex items-center justify-center min-h-[100px] mb-3">
              {form.signature_image ? (
                <div className="relative">
                  <img src={form.signature_image} alt="Timbro e firma" className="max-h-20 max-w-full" data-testid="company-signature-preview" />
                  <button onClick={() => update("signature_image", "")} className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-1 text-slate-500 hover:text-red-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="text-center text-slate-400">
                  <ImageIcon className="w-7 h-7 mx-auto mb-1" />
                  <span className="text-xs">Nessun timbro/firma</span>
                </div>
              )}
            </div>
            <input ref={sigRef} type="file" accept="image/*" onChange={onSignature} className="hidden" data-testid="company-signature-input" />
            <Button variant="outline" className="w-full" onClick={() => sigRef.current?.click()} data-testid="company-signature-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Carica timbro e firma
            </Button>
          </div>

          <div className="mt-5">
            <Label className="text-xs">Colore brand (linea & accenti)</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" value={form.brand_color || "#5B9BD5"} onChange={(e) => update("brand_color", e.target.value)}
                data-testid="brand-color-input" className="w-10 h-10 rounded border border-slate-200 cursor-pointer" />
              <Input value={form.brand_color || ""} onChange={(e) => update("brand_color", e.target.value)} placeholder="#5B9BD5" />
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm p-5">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Dati fiscali</h2>
          <p className="text-sm text-slate-500 mb-4">Appariranno nel piè di pagina di ogni preventivo.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.k} className={f.k === "address" ? "md:col-span-2" : ""}>
                <Label className="text-xs">{f.label}</Label>
                <Input data-testid={`company-${f.k}`} value={form[f.k] || ""}
                  onChange={(e) => update(f.k, e.target.value)} placeholder={f.ph} className="mt-1" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
